/* eslint-disable no-case-declarations */
import {
  type State,
  reactive,
  watchEffect,
  appendUniqueArray,
  deleteArrayItem,
  includes,
  flushArray,
  isJustObject
} from "@zyzgroup/core-common";
import { isInputCheckbox, isInputRadio, isSelect } from "./hooks";

// Model-View-ViewModel 响应式系统
export class MVVM {
  state: State;
  el: HTMLElement;

  constructor(state: State, el: HTMLElement) {
    this.state = reactive(state, { deep: true });
    this.el = el;
    this.compile(this.el);
  }

  // 解析器
  compile(el: HTMLElement) {
    el.childNodes.forEach((node) => {
      switch (node.nodeType) {
        case 1: // Element Node 元素节点
          const elementNode = node as HTMLElement;
          [...elementNode.attributes].forEach((attr) => {
            const attrName = attr.name;
            const attrValue = attr.value;
            if (attrName === "v-model") {
              // 数据双向绑定
              if (isInputRadio(elementNode)) {
                // radio 使用 checked 属性 和 change 事件
                const finalNode = elementNode as HTMLInputElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.checked =
                    this.getStateValue(attrValue) == finalNode.value;
                });
                // 通过事件监听，视图 驱动 数据 更新
                finalNode.addEventListener("change", (e) => {
                  this.setStateValue(
                    attrValue,
                    (e.target as typeof finalNode).value
                  );
                });
              } else if (isInputCheckbox(elementNode)) {
                // checkbox 使用 checked 属性 和 change 事件
                const finalNode = elementNode as HTMLInputElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.checked = includes(
                    this.getStateValue(attrValue),
                    finalNode.value
                  );
                });
                // 通过事件监听，视图 驱动 数据 更新
                finalNode.addEventListener("change", (e) => {
                  const target = e.target as typeof finalNode;
                  const value = target.value;
                  if (target.checked) {
                    appendUniqueArray(this.getStateValue(attrValue), value);
                  } else {
                    deleteArrayItem(this.getStateValue(attrValue), value);
                  }
                });
              } else if (isSelect(elementNode)) {
                // select 使用 value 属性 和 change 事件
                const finalNode = elementNode as HTMLSelectElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.value = this.getStateValue(attrValue);
                });
                // 通过事件监听，视图 驱动 数据 更新
                finalNode.addEventListener("change", (e) => {
                  this.setStateValue(
                    attrValue,
                    (e.target as typeof finalNode).value
                  );
                });
              } else {
                // input text number ...  和 textarea 使用 value 属性 和 input 事件
                const finalNode = elementNode as
                  | HTMLInputElement
                  | HTMLTextAreaElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.value = this.getStateValue(attrValue);
                });
                // 通过事件监听，视图 驱动 数据 更新
                const cb = (e: Event) => {
                  this.setStateValue(
                    attrValue,
                    (e.target as typeof finalNode).value
                  );
                };
                finalNode.addEventListener("input", cb);
                // finalNode.addEventListener("change", cb);
              }
            } else if (attrName === "v-text") {
              // 数据 驱动 视图 更新
              watchEffect(() => {
                elementNode.innerText = this.getStateValue(attrValue);
              });
            } else if (attrName === "v-html") {
              // 数据 驱动 视图 更新
              watchEffect(() => {
                elementNode.innerHTML = this.getStateValue(attrValue);
              });
            }
          });
          // 递归子节点
          if (elementNode.childNodes.length > 0) {
            this.compile(elementNode);
          }
          break;
        case 3: // Text Node 文本节点
          const textContent = node.textContent || "";
          const reg = /\{\{\s*([^{}\s]+)\s*\}\}/g;
          const matches = reg.exec(textContent);
          if (matches && matches[1]) {
            // 数据 驱动 视图 更新
            watchEffect(() => {
              node.textContent = textContent.replace(
                reg,
                this.getStateValue(matches[1])
              );
            });
          }
          break;
      }
    });
  }

  // UNDO : 暂未考虑 嵌套的 数组
  getStateValue(attrName: string) {
    let v: any = this.state;
    attrName.split(".").forEach((name) => {
      v = v[name];
    });
    return v;
  }

  // UNDO : 暂未考虑 嵌套的 数组
  setStateValue(attrName: string, val: any) {
    let v: any = this.state;
    const names = attrName.split(".");
    const c = names.length;
    for (let i = 0; i < c - 1; i++) {
      v = v[names[i]];
    }
    v[names[c - 1]] = val;
  }

  // setState(data: State) {
  //   for (const k in data) {
  //     const v = data[k];
  //     if (Array.isArray(v)) {
  //       flushArray(this.state[k], v);
  //     } else if (isJustObject(v)) {
  //       for (const vk in v) {
  //         this.state[k][vk] = v[vk];
  //       }
  //     } else {
  //       this.state[k] = v;
  //     }
  //   }
  // }

  /**
   * @description 编译模板字符串
   * @example
   * const template = `
   * <ul>
   *   <% for(let i=0; i < data.supplies.length; i++) { %>
   *     <li><%= data.supplies[i] %></li>
   *   <% } %>
   * </ul>
   * `;
   * const parse = eval(compileTemplate(template));
   * console.log(parse({ supplies: ["broom", "mop", "cleaner"] }));
   */
  static compileTemplate(template: string) {
    template = template
      .replace(/<%=(.+?)%>/g, "`); \n  echo( $1 ); \n  echo(`")
      .replace(/<%([\s\S]+?)%>/g, "`); \n $1 \n  echo(`");
    template = "echo(`" + template + "`);";
    const script = `(function parse(data){
      let output = "";
      function echo(html){
        output += html;
      }
      ${template}
      return output;
    })`;
    return script;
  }
}

export default MVVM;
